import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isApiError } from './lib/api';
import { useAdminMfaStore } from './stores/adminMfaStore';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ToastProvider } from './components/ui/Toast';
import { ConfirmDialogProvider } from './components/ui/ConfirmDialog';
import { installGlobalErrorReporting } from './lib/errorReporting';
import { PwaHost } from './components/pwa';
import './index.css';

installGlobalErrorReporting();

function handleGlobalApiError(error: unknown) {
  if (isApiError(error) && error.status === 403 && error.code === 'MFA_REQUIRED') {
    useAdminMfaStore.getState().setMfaRequired(true);
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleGlobalApiError }),
  mutationCache: new MutationCache({ onError: handleGlobalApiError }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      // Erros de cliente (4xx: não encontrado, sem permissão, MFA) não melhoram com nova tentativa
      retry: (failureCount, error) => {
        if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <ConfirmDialogProvider>
            <App />
            <PwaHost />
          </ConfirmDialogProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);
